import React, { useState, useEffect } from 'react';

const COLORS = {
  primary: '#2D5016',
  primaryLight: '#4A7C2B',
  secondary: '#8BA888',
  background: '#F5F7F3',
  text: '#2D3E1F',
  textLight: '#6B7F5C',
  border: '#D4E0CE',
  white: '#FFFFFF',
  error: '#C44536',
};

const SUPABASE_FUNCTIONS_URL = 'https://ztbeipyycwswknnzqyze.supabase.co/functions/v1';

const VirbalSignup = () => {
  const [step, setStep] = useState(1); // 1 = info, 2 = verify, 3 = questions
  
  // Step 1: Partner info
  const [partner1FirstName, setPartner1FirstName] = useState('');
  const [partner1LastName, setPartner1LastName] = useState('');
  const [partner1Phone, setPartner1Phone] = useState('');
  const [partner2FirstName, setPartner2FirstName] = useState('');
  const [partner2LastName, setPartner2LastName] = useState('');
  const [partner2Phone, setPartner2Phone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [dropTime, setDropTime] = useState('19:00');
  const [timezone, setTimezone] = useState('America/Chicago');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [selectedPlan] = useState('standard'); // Single plan for soft launch

  // Step 2: Verification
  const [verificationCode, setVerificationCode] = useState('');

  // Step 3: Questions
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});

  const [loading, setLoading] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Detect timezone
    const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimezone(detectedTz);
  }, []);

  useEffect(() => {
    if (step === 3 && questions.length === 0) {
      loadQuestions();
    }
  }, [step]);

  const loadQuestions = async () => {
    setLoadingQuestions(true);
    // 15 questions - removed conflict_style, inside_world, describe_yourself, core_value, desired_habit, skill_to_master
    const hardcodedQuestions = [
      // RELATIONSHIP BASICS
      { question_key: 'relationship_goal', question_text: "What's your goal for Virbal?", input_type: 'select', options: ["Go deeper", "Stay close through a busy season", "Build the habit early (new relationship)", "Keep things alive and interesting"], required: true },
      { question_key: 'relationship_length', question_text: 'How long have you been together?', input_type: 'select', options: ["Less than a year", "1–3 years", "4–10 years", "10+ years"], required: true },
      { question_key: 'proximity', question_text: 'Are you in the same city or long-distance?', input_type: 'select_with_followup', options: ["Same city", "Long-distance"], required: true },
      { question_key: 'how_we_met', question_text: 'How did you first meet?', input_type: 'text', required: true },
      { question_key: 'important_dates', question_text: 'What are the dates that matter most to you two? i.e. anniversaries, birthdays, major events', input_type: 'important_dates', required: false },
      
      // THE RELATIONSHIP ITSELF
      { question_key: 'unappreciated_actions', question_text: "What's one thing your partner does that you are especially grateful for?", input_type: 'textarea', required: true },
      { question_key: 'love_languages_receive', question_text: 'How do you prefer to feel loved?', input_type: 'multiselect', options: ["Words of Affirmation - verbal expressions of love, appreciation, and encouragement", "Acts of Service - your partner doing helpful things for you", "Receiving Gifts - receiving meaningful tokens of affection", "Quality Time - receiving undivided, focused attention", "Physical Touch - physical connection like hugs, holding hands, etc."], required: true },
      { question_key: 'love_languages_give', question_text: 'How do you naturally show love?', input_type: 'multiselect', options: ["Words of Affirmation - verbal expressions of love, appreciation, and encouragement", "Acts of Service - doing helpful things for your partner", "Giving Gifts - giving meaningful tokens of affection", "Quality Time - giving undivided, focused attention", "Physical Touch - physical connection like hugs, holding hands, etc."], required: true },
      { question_key: 'time_spend', question_text: 'When you two have free time together, what does it usually look like?', input_type: 'select', options: ["We're homebodies", "We like getting out", "Mix of both", "We don't get much free time right now"], required: true },
      { question_key: 'current_challenges', question_text: 'Is there anything you two are navigating together right now - new job, new baby, a tough season, a big transition?', input_type: 'textarea', required: false },
      
      // SHARED HISTORY
      { question_key: 'shared_memory', question_text: "What's a memory you two share that still makes you laugh - or one that still means a lot to you?", input_type: 'textarea', required: false },
      
      // WHO YOU ARE
      { question_key: 'perfect_day', question_text: 'If you could design a perfect day together, what would it look like?', input_type: 'textarea', required: true },
      { question_key: 'feel_alive', question_text: 'What is one activity or moment that makes you feel most alive and energized?', input_type: 'textarea', required: true },
      
      // DREAMS & GROWTH
      { question_key: 'big_goals', question_text: 'What are 2 or 3 big goals or dreams you have for the next 5–10 years?', input_type: 'textarea', required: true },
      { question_key: 'proud_accomplishments', question_text: "What are a few things you're really proud of accomplishing - big or small?", input_type: 'textarea', required: true },
    ];
    setQuestions(hardcodedQuestions);
    setLoadingQuestions(false);
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!smsOptIn) {
      setError('You must agree to receive SMS messages to use Virbal');
      return;
    }

    setLoading(true);

    try {
      // Send verification code to Partner 1 only
      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partner1Phone }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send verification code');
      }

      setStep(2);
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setError('');

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setLoading(true);

    try {
      // Verify Partner 1's code
      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/verify-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          partner1Phone,
          partner1Code: verificationCode,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Invalid verification code');
      }

      setStep(3);
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionKey, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionKey]: value
    }));
  };

  const handleStep3Submit = async (e) => {
    e.preventDefault();
    setError('');

    const requiredQuestions = questions.filter(q => q.required);
    const missingAnswers = requiredQuestions.filter(q => {
      const answer = answers[q.question_key];
      if (!answer) return true;
      
      // Handle arrays (multiselect)
      if (Array.isArray(answer)) return answer.length === 0;
      
      // Handle objects (select_with_followup, important_dates)
      if (typeof answer === 'object') {
        // For select_with_followup, check if selection exists
        if (answer.selection !== undefined) return !answer.selection;
        // For important_dates, check if at least one entry has both label and date
        if (answer.label !== undefined || answer.date !== undefined) {
          return !answer.label || !answer.date;
        }
        return false;
      }
      
      // Handle strings
      return typeof answer === 'string' && answer.trim() === '';
    });
    
    if (missingAnswers.length > 0) {
      setError(`Please answer all required questions (${missingAnswers.length} remaining)`);
      return;
    }

    setLoading(true);

    try {
      // Create Stripe Checkout session
      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/create-stripe-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          subscriptionTier: selectedPlan,
          partner1FirstName,
          partner1LastName,
          partner2FirstName,
          partner2LastName,
          partner1Phone,
          partner2Phone,
          password,
          dropTime,
          timezone,
          partner1Answers: answers,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = url;

    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message || 'Failed to proceed to checkout. Please try again.');
      setLoading(false);
    }
  };

  const renderQuestionInput = (question) => {
    const value = answers[question.question_key] || '';

    switch (question.input_type) {
      case 'textarea':
        return (
          <textarea
            id={`question-${question.id}`}
            value={value}
            onChange={(e) => handleAnswerChange(question.question_key, e.target.value)}
            placeholder="Your answer..."
            rows="4"
            required={question.required}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `2px solid ${COLORS.border}`,
              borderRadius: '6px',
              fontSize: '1rem',
              fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
              resize: 'vertical',
            }}
          />
        );

      case 'select':
        const options = question.options || [];
        return (
          <select
            id={`question-${question.id}`}
            value={value}
            onChange={(e) => handleAnswerChange(question.question_key, e.target.value)}
            required={question.required}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `2px solid ${COLORS.border}`,
              borderRadius: '6px',
              fontSize: '1rem',
              fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%232D5016' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 1rem center',
              paddingRight: '2.5rem',
            }}
          >
            <option value="">Select an option...</option>
            {options.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            id={`question-${question.id}`}
            value={value}
            onChange={(e) => handleAnswerChange(question.question_key, e.target.value)}
            required={question.required}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `2px solid ${COLORS.border}`,
              borderRadius: '6px',
              fontSize: '1rem',
              fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
            }}
          />
        );

      case 'multi_date':
        return (
          <textarea
            id={`question-${question.id}`}
            value={value}
            onChange={(e) => handleAnswerChange(question.question_key, e.target.value)}
            placeholder="Enter dates (e.g., First Date: 2020-01-15, Wedding: 2022-06-10)"
            rows="3"
            required={question.required}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `2px solid ${COLORS.border}`,
              borderRadius: '6px',
              fontSize: '1rem',
              fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
              resize: 'vertical',
            }}
          />
        );

      case 'multiselect':
        const multiselectOptions = question.options || [];
        const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {multiselectOptions.map((option, idx) => (
              <label key={idx} style={{
                display: 'flex',
                alignItems: 'flex-start',
                cursor: 'pointer',
                padding: '0.75rem',
                border: `2px solid ${selectedValues.includes(option) ? COLORS.primary : COLORS.border}`,
                borderRadius: '6px',
                background: selectedValues.includes(option) ? 'rgba(45, 80, 22, 0.05)' : 'transparent',
                transition: 'all 0.2s',
              }}>
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...selectedValues, option]
                      : selectedValues.filter(v => v !== option);
                    handleAnswerChange(question.question_key, newValues);
                  }}
                  style={{
                    marginRight: '0.75rem',
                    marginTop: '0.25rem',
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px',
                    accentColor: COLORS.primary,
                  }}
                />
                <span style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.95rem',
                  color: COLORS.text,
                  lineHeight: 1.4,
                }}>
                  {option}
                </span>
              </label>
            ))}
          </div>
        );

      case 'important_dates':
        const dates = Array.isArray(value) ? value : (value ? [value] : [{ label: '', date: '' }]);
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {dates.map((dateEntry, idx) => (
              <div key={idx} style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                padding: '1rem',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '6px',
                background: 'rgba(255, 255, 255, 0.5)',
              }}>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="e.g., Anniversary, Birthday"
                    value={dateEntry.label || ''}
                    onChange={(e) => {
                      const newDates = [...dates];
                      newDates[idx] = { ...newDates[idx], label: e.target.value };
                      handleAnswerChange(question.question_key, newDates);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      border: `2px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontFamily: 'Inter, sans-serif',
                    }}
                  />
                  {dates.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newDates = dates.filter((_, i) => i !== idx);
                        handleAnswerChange(question.question_key, newDates);
                      }}
                      style={{
                        padding: '0.75rem 1rem',
                        background: COLORS.error,
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.9rem',
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  type="date"
                  value={dateEntry.date || ''}
                  onChange={(e) => {
                    const newDates = [...dates];
                    newDates[idx] = { ...newDates[idx], date: e.target.value };
                    handleAnswerChange(question.question_key, newDates);
                  }}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: `2px solid ${COLORS.border}`,
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontFamily: 'Inter, sans-serif',
                  }}
                />
              </div>
            ))}
            {dates.length < 3 && (
              <button
                type="button"
                onClick={() => {
                  const newDates = [...dates, { label: '', date: '' }];
                  handleAnswerChange(question.question_key, newDates);
                }}
                style={{
                  padding: '0.75rem 1rem',
                  background: 'transparent',
                  color: COLORS.primary,
                  border: `2px solid ${COLORS.primary}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                }}
              >
                + Add Another Date
              </button>
            )}
          </div>
        );

      case 'select_with_followup':
        const followupSelectOptions = question.options || [];
        return (
          <div>
            <select
              id={`question-${question.id}`}
              value={value.selection || ''}
              onChange={(e) => handleAnswerChange(question.question_key, { ...value, selection: e.target.value })}
              required={question.required}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                border: `2px solid ${COLORS.border}`,
                borderRadius: '6px',
                fontSize: '1rem',
                fontFamily: 'Inter, sans-serif',
                boxSizing: 'border-box',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%232D5016' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 1rem center',
                paddingRight: '2.5rem',
                marginBottom: '1rem',
              }}
            >
              <option value="">Select an option...</option>
              {followupSelectOptions.map((option, idx) => (
                <option key={idx} value={option}>{option}</option>
              ))}
            </select>
            {value.selection && (
              <input
                type="text"
                placeholder="Your city..."
                value={value.city || ''}
                onChange={(e) => handleAnswerChange(question.question_key, { ...value, city: e.target.value })}
                required={question.required}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: `2px solid ${COLORS.border}`,
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontFamily: 'Inter, sans-serif',
                  boxSizing: 'border-box',
                }}
              />
            )}
          </div>
        );

      default:
        return (
          <input
            type="text"
            id={`question-${question.id}`}
            value={value}
            onChange={(e) => handleAnswerChange(question.question_key, e.target.value)}
            placeholder="Your answer..."
            required={question.required}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              border: `2px solid ${COLORS.border}`,
              borderRadius: '6px',
              fontSize: '1rem',
              fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
            }}
          />
        );
    }
  };

  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        background: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
        fontFamily: 'Georgia, serif',
      }}>
        <div style={{
          maxWidth: '500px',
          background: COLORS.white,
          borderRadius: '8px',
          padding: '3rem 2rem',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(45, 80, 22, 0.08)',
        }}>
          <img 
            src="https://static.wixstatic.com/media/e78d43_e6a2a4d725014972851fe9dd56aeaa4b~mv2.png"
            alt="Virbal"
            style={{ maxWidth: '200px', marginBottom: '2rem' }}
          />
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: COLORS.primary,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '3rem',
            margin: '0 auto 2rem',
          }}>✓</div>
          <h2 style={{ fontSize: '2rem', color: COLORS.text, marginBottom: '1.5rem', fontWeight: 400 }}>
            Almost There!
          </h2>
          <p style={{
            color: COLORS.textLight,
            fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
            fontSize: '1.1rem',
            lineHeight: 1.6,
            marginBottom: '1rem',
          }}>
            We've sent {partner2FirstName} a text with a link to complete their signup.
          </p>
          <p style={{
            color: COLORS.textLight,
            fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
            fontSize: '1.1rem',
            lineHeight: 1.6,
          }}>
            Once they finish, you'll both start receiving daily prompts!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: COLORS.background,
      padding: '2rem 1rem',
      fontFamily: 'Georgia, serif',
    }}>
      <div style={{
        maxWidth: '650px',
        margin: '0 auto',
        background: COLORS.white,
        borderRadius: '8px',
        padding: '3rem 2.5rem',
        boxShadow: '0 2px 8px rgba(45, 80, 22, 0.08)',
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '2.5rem',
          paddingBottom: '2rem',
          borderBottom: `2px solid ${COLORS.border}`,
        }}>
          <img 
            src="https://static.wixstatic.com/media/e78d43_e6a2a4d725014972851fe9dd56aeaa4b~mv2.png"
            alt="Virbal"
            style={{ maxWidth: '200px', marginBottom: '1rem' }}
          />
          <p style={{
            color: COLORS.textLight,
            fontStyle: 'italic',
            fontSize: '1rem',
            fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
          }}>
            Start Your Journey Together
          </p>
        </div>

        {/* Progress Dots */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          marginBottom: '2.5rem',
        }}>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: step >= 1 ? COLORS.primary : COLORS.border,
          }}></div>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: step >= 2 ? COLORS.primary : COLORS.border,
          }}></div>
          <div style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            background: step >= 3 ? COLORS.primary : COLORS.border,
          }}></div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#FFF5F5',
            border: '1px solid #FED7D7',
            color: COLORS.error,
            padding: '1rem',
            borderRadius: '6px',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
            fontSize: '0.95rem',
          }}>
            <span style={{
              background: COLORS.error,
              color: 'white',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              flexShrink: 0,
            }}>!</span>
            {error}
          </div>
        )}

        {/* Step 1: Partner Info */}
        {step === 1 && (
          <form onSubmit={handleStep1Submit}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', color: COLORS.text, marginBottom: '0.75rem', fontWeight: 400 }}>
                Let's Get Started
              </h2>
              <p style={{ color: COLORS.textLight, fontFamily: 'Inter, sans-serif', fontSize: '1rem' }}>
                Tell us about you and your partner
              </p>
            </div>

            {/* Partner 1 */}
            <div style={{
              marginBottom: '2rem',
              padding: '1.5rem',
              background: 'rgba(45, 80, 22, 0.02)',
              borderRadius: '6px',
              border: `1px solid ${COLORS.border}`,
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                color: COLORS.text,
                marginBottom: '1rem',
                fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                fontWeight: 600,
              }}>Your Information</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    color: COLORS.text,
                    marginBottom: '0.5rem',
                    fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                  }}>First Name *</label>
                  <input
                    type="text"
                    value={partner1FirstName}
                    onChange={(e) => setPartner1FirstName(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: `2px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    color: COLORS.text,
                    marginBottom: '0.5rem',
                    fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                  }}>Last Name *</label>
                  <input
                    type="text"
                    value={partner1LastName}
                    onChange={(e) => setPartner1LastName(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: `2px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  color: COLORS.text,
                  marginBottom: '0.5rem',
                  fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                }}>Phone Number *</label>
                <input
                  type="tel"
                  value={partner1Phone}
                  onChange={(e) => setPartner1Phone(e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: `2px solid ${COLORS.border}`,
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            {/* Partner 2 */}
            <div style={{
              marginBottom: '2rem',
              padding: '1.5rem',
              background: 'rgba(45, 80, 22, 0.02)',
              borderRadius: '6px',
              border: `1px solid ${COLORS.border}`,
            }}>
              <h3 style={{
                fontSize: '1.1rem',
                color: COLORS.text,
                marginBottom: '1rem',
                fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                fontWeight: 600,
              }}>Your Partner's Information</h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    color: COLORS.text,
                    marginBottom: '0.5rem',
                    fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                  }}>First Name *</label>
                  <input
                    type="text"
                    value={partner2FirstName}
                    onChange={(e) => setPartner2FirstName(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: `2px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    color: COLORS.text,
                    marginBottom: '0.5rem',
                    fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                  }}>Last Name *</label>
                  <input
                    type="text"
                    value={partner2LastName}
                    onChange={(e) => setPartner2LastName(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: `2px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  color: COLORS.text,
                  marginBottom: '0.5rem',
                  fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                }}>Phone Number *</label>
                <input
                  type="tel"
                  value={partner2Phone}
                  onChange={(e) => setPartner2Phone(e.target.value)}
                  placeholder="(555) 123-4567"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: `2px solid ${COLORS.border}`,
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            {/* Account Settings */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                fontSize: '1.1rem',
                color: COLORS.text,
                marginBottom: '1rem',
                fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                fontWeight: 600,
              }}>Account Settings</h3>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  color: COLORS.text,
                  marginBottom: '0.5rem',
                  fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                }}>Email *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: `2px solid ${COLORS.border}`,
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '0.9rem',
                  color: COLORS.text,
                  marginBottom: '0.5rem',
                  fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                }}>Password *</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength="6"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: `2px solid ${COLORS.border}`,
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    color: COLORS.text,
                    marginBottom: '0.5rem',
                    fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                  }}>Drop Time *</label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <select
                      value={dropTime.split(':')[0]}
                      onChange={(e) => {
                        const hour = e.target.value;
                        setDropTime(`${hour}:00`);
                      }}
                      required
                      style={{
                        flex: 1,
                        padding: '0.75rem 1rem',
                        border: `2px solid ${COLORS.border}`,
                        borderRadius: '6px',
                        fontSize: '1rem',
                        fontFamily: 'Inter, sans-serif',
                        boxSizing: 'border-box',
                      }}
                    >
                      <option value="08">8 AM</option>
                      <option value="09">9 AM</option>
                      <option value="10">10 AM</option>
                      <option value="11">11 AM</option>
                      <option value="12">12 PM</option>
                      <option value="13">1 PM</option>
                      <option value="14">2 PM</option>
                      <option value="15">3 PM</option>
                      <option value="16">4 PM</option>
                      <option value="17">5 PM</option>
                      <option value="18">6 PM</option>
                      <option value="19">7 PM</option>
                      <option value="20">8 PM</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.9rem',
                    color: COLORS.text,
                    marginBottom: '0.5rem',
                    fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                  }}>Timezone</label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: `2px solid ${COLORS.border}`,
                      borderRadius: '6px',
                      fontSize: '1rem',
                      fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                    }}
                  >
                    <option value="America/New_York">Eastern</option>
                    <option value="America/Chicago">Central</option>
                    <option value="America/Denver">Mountain</option>
                    <option value="America/Los_Angeles">Pacific</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Plan Display */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                fontSize: '1.1rem',
                color: COLORS.text,
                marginBottom: '1rem',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
              }}>Your Plan</h3>

              {/* Single Standard Plan */}
              <div style={{
                padding: '1.5rem',
                border: `3px solid ${COLORS.primary}`,
                borderRadius: '8px',
                background: 'rgba(45, 80, 22, 0.05)',
                boxSizing: 'border-box',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <h4 style={{
                    fontSize: '1.2rem',
                    color: COLORS.text,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                  }}>Standard</h4>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: COLORS.primary,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                  }}>✓</div>
                </div>
                <p style={{
                  fontSize: '1.5rem',
                  color: COLORS.primary,
                  fontWeight: 600,
                  marginBottom: '1rem',
                }}>$8<span style={{ fontSize: '1rem', color: COLORS.textLight, fontWeight: 400 }}>/mo</span></p>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.9rem',
                  color: COLORS.text,
                  lineHeight: 1.8,
                }}>
                  <li>✓ 3 Drops/week (M, W, F)</li>
                  <li>✓ 3 @virbal texts/day</li>
                  <li>✓ 2 @date texts/day</li>
                  <li>✓ 1 Weekly Recap</li>
                  <li>✓ 1 Monthly Recap</li>
                </ul>
              </div>

              <p style={{
                marginTop: '1rem',
                fontSize: '0.85rem',
                color: COLORS.textLight,
                fontFamily: 'Inter, sans-serif',
                textAlign: 'center',
              }}>
                7-day free trial • Cancel anytime
              </p>
            </div>

            {/* SMS Opt-in */}
            <div style={{
              marginBottom: '2rem',
              padding: '1rem',
              background: 'rgba(45, 80, 22, 0.05)',
              borderRadius: '6px',
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                fontSize: '0.9rem',
                color: COLORS.text,
              }}>
                <input
                  type="checkbox"
                  checked={smsOptIn}
                  onChange={(e) => setSmsOptIn(e.target.checked)}
                  required
                  style={{
                    marginTop: '0.25rem',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                  }}
                />
                <span>
                  I agree to receive SMS messages from Virbal with daily conversation prompts. Message and data rates may apply. *
                </span>
              </label>
            </div>

            {/* Terms & Conditions Checkbox */}
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'rgba(45, 80, 22, 0.02)',
              borderRadius: '6px',
              border: `1px solid ${COLORS.border}`,
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                cursor: 'pointer',
                fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                fontSize: '0.9rem',
                color: COLORS.text,
              }}>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  required
                  style={{
                    marginTop: '0.25rem',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: COLORS.primary,
                  }}
                />
                <span>
                  By checking I agree to the{' '}
                  <a
                    href="https://www.virbal.net/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: COLORS.primary,
                      textDecoration: 'underline',
                      fontWeight: 600,
                    }}
                  >
                    Terms & Conditions
                  </a>
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '1rem 2rem',
                background: loading ? COLORS.border : COLORS.primary,
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1.05rem',
                fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = COLORS.primaryLight)}
              onMouseLeave={(e) => !loading && (e.target.style.background = COLORS.primary)}
            >
              {loading ? 'Sending Code...' : 'Continue'}
            </button>
          </form>
        )}

        {/* Step 2: Verification */}
        {step === 2 && (
          <form onSubmit={handleStep2Submit}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', color: COLORS.text, marginBottom: '0.75rem', fontWeight: 400 }}>
                Verify Your Number
              </h2>
              <p style={{ color: COLORS.textLight, fontFamily: 'Inter, sans-serif', fontSize: '1rem' }}>
                Enter the 6-digit code sent to {partner1Phone}
              </p>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.95rem',
                color: COLORS.text,
                marginBottom: '0.5rem',
                fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                fontWeight: 500,
              }}>Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength="6"
                required
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: `2px solid ${COLORS.border}`,
                  borderRadius: '6px',
                  fontSize: '1.5rem',
                  textAlign: 'center',
                  letterSpacing: '0.5em',
                  fontWeight: 600,
                  fontFamily: 'Courier New, monospace',
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '1rem 2rem',
                background: loading ? COLORS.border : COLORS.primary,
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1.05rem',
                fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = COLORS.primaryLight)}
              onMouseLeave={(e) => !loading && (e.target.style.background = COLORS.primary)}
            >
              {loading ? 'Verifying...' : 'Verify & Continue'}
            </button>
          </form>
        )}

        {/* Step 3: Questions */}
        {step === 3 && (
          <form onSubmit={handleStep3Submit}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', color: COLORS.text, marginBottom: '0.75rem', fontWeight: 400 }}>
                Tell Us About Your Relationship
              </h2>
              <p style={{ color: COLORS.textLight, fontFamily: 'Inter, sans-serif', fontSize: '1rem', lineHeight: 1.6, maxWidth: '600px', margin: '0 auto' }}>
                These 15 questions will help us get to know you and what is important to you for the growth of your relationship. It should only take 8-12min to complete but the investment will be 100 fold!
              </p>
            </div>

            {loadingQuestions ? (
              <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  border: `4px solid ${COLORS.border}`,
                  borderTop: `4px solid ${COLORS.primary}`,
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 1rem',
                }}></div>
                <p style={{ color: COLORS.textLight, fontFamily: 'Inter, sans-serif' }}>
                  Loading questions...
                </p>
              </div>
            ) : (
              <div style={{ marginBottom: '2rem' }}>
                {questions.map((question, index) => (
                  <div key={question.id} style={{
                    marginBottom: '1.75rem',
                    padding: '1.5rem',
                    background: 'rgba(45, 80, 22, 0.02)',
                    borderRadius: '6px',
                    border: `1px solid ${COLORS.border}`,
                  }}>
                    <label htmlFor={`question-${question.id}`} style={{
                      display: 'block',
                      fontSize: '1.05rem',
                      color: COLORS.text,
                      marginBottom: '1rem',
                      lineHeight: 1.6,
                    }}>
                      <span style={{
                        display: 'inline-block',
                        background: COLORS.primary,
                        color: 'white',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        textAlign: 'center',
                        lineHeight: '28px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                        marginRight: '0.75rem',
                      }}>
                        {index + 1}
                      </span>
                      {question.question_text}
                      {question.required && (
                        <span style={{ color: COLORS.primary, marginLeft: '0.25rem' }}>*</span>
                      )}
                    </label>
                    {renderQuestionInput(question)}
                  </div>
                ))}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || loadingQuestions}
              style={{
                width: '100%',
                padding: '1rem 2rem',
                background: loading ? COLORS.border : COLORS.primary,
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1.05rem',
                fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: loading ? 0.6 : 1,
              }}
              onMouseEnter={(e) => !loading && (e.target.style.background = COLORS.primaryLight)}
              onMouseLeave={(e) => !loading && (e.target.style.background = COLORS.primary)}
            >
              {loading ? 'Creating Account...' : 'Complete Signup'}
            </button>
          </form>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VirbalSignup;
